import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { identity } from '@repletoryreach/sdk-api';
import { Stack, Card, Title, Button, Group, Text, Badge, Code } from '@repletoryreach/sdk-ui';
import { Link } from 'react-router-dom';
import { DataTable, Alert, EmptyState, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Drawer, Tooltip, Modal } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput, Select, NumberInput, Switch } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { useCredits } from '../../../lib/credits';
import type { Indexed } from '../../../lib/types';
import { useT } from '../../../i18n/I18nProvider';
import { RevealOnce } from '../../../components/RevealOnce';
import './api-keys.css';
import { publicApiUrl } from '../../../lib/public-api';

/**
 * Scopes a tenant may grant.
 *
 * §4 item 2: every key the console could create had exactly
 * `['messages:send', 'contacts:read']`, hardcoded, never asked about and never
 * displayed, so a tenant needing a read-only key or one that manages templates
 * could not make it here at all.
 */
const SCOPES = [
  { value: 'messages:send', labelKey: 'settings.apiKeys.scope.messagesSend' },
  { value: 'messages:read', labelKey: 'settings.apiKeys.scope.messagesRead' },
  { value: 'contacts:read', labelKey: 'settings.apiKeys.scope.contactsRead' },
  { value: 'contacts:write', labelKey: 'settings.apiKeys.scope.contactsWrite' },
  { value: 'templates:read', labelKey: 'settings.apiKeys.scope.templatesRead' },
  // 2 October 2026 (owner's decision): broadcasts can be watched without being
  // started, and the media library and webhook subscriptions are on the API.
  { value: 'broadcasts:read', labelKey: 'settings.apiKeys.scope.broadcastsRead' },
  { value: 'broadcasts:write', labelKey: 'settings.apiKeys.scope.broadcastsWrite' },
  { value: 'media', labelKey: 'settings.apiKeys.scope.media' },
  { value: 'webhooks', labelKey: 'settings.apiKeys.scope.webhooks' },
  // 7 October 2026: bookings on the API, a read and a write that includes it.
  { value: 'bookings:read', labelKey: 'settings.apiKeys.scope.bookingsRead' },
  { value: 'bookings:write', labelKey: 'settings.apiKeys.scope.bookingsWrite' },
  // 7 October 2026: tickets on the API, a read and a write that includes it.
  { value: 'tickets:read', labelKey: 'settings.apiKeys.scope.ticketsRead' },
  { value: 'tickets:write', labelKey: 'settings.apiKeys.scope.ticketsWrite' },
  // 14 October 2026: the AI API, charged to the business's credits, with an optional daily limit per key.
  { value: 'ai', labelKey: 'settings.apiKeys.scope.ai' }
];

/** Credits typed in a form, as minor units (1 credit = R1 = 100 minor), or null for none. */
const toMinor = (v: number | '') => (typeof v === 'number' && v > 0 ? Math.round(v * 100) : null);

const EXPIRY_CHOICES = ['never', '30', '90', '365'] as const;

export default function SettingsApiKeysPage() {
  const t = useT();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [freshKey, setFreshKey] = useState<string | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const [limitFor, setLimitFor] = useState<identity.ApiKey | null>(null);
  const credits = useCredits();

  const { data, isPending, error } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => identity.listApiKeys()
  });

  const all = (data?.data ?? []) as Indexed<identity.ApiKey>[];
  // §4 item 9: revoked keys were never filtered and could not be hidden, so
  // the table grew monotonically with dead rows.
  const rows = useMemo(() => (showRevoked ? all : all.filter((k) => k.is_active)), [all, showRevoked]);
  const revokedCount = all.length - all.filter((k) => k.is_active).length;

  const revoke = useMutation({
    mutationFn: (k: identity.ApiKey) => identity.revokeApiKey(k.id),
    onSuccess: (_r, k) => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      notifySuccess(t('settings.apiKeys.toast.revokedNamed', { name: k.name }), t('settings.nav.apiKeys'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('settings.apiKeys.revokeFailed'), t('settings.nav.apiKeys'))
  });

  return (
    <Stack gap="md">
      {/*
        §4 item 1, the headline defect: the key was rendered in a code block
        and the user was expected to select it with a mouse. No copy button, no
        download, no confirmation, and no success toast either, so if they
        missed this Alert or navigated away the key was unrecoverable.
      */}
      {freshKey && (
        <RevealOnce
          value={freshKey}
          title={t('settings.apiKeys.modal.created.title')}
          description={t('settings.apiKeys.modal.created.body')}
          filename="repletoryreach-api-key"
          onDismiss={() => setFreshKey(null)}
        />
      )}

      <Card withBorder data-guide="apikeys">
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Title order={4}>{t('settings.nav.apiKeys')}</Title>
            <Group gap="sm">
              {revokedCount > 0 && (
                <Switch
                  label={t('settings.apiKeys.showRevoked', { count: revokedCount })}
                  checked={showRevoked}
                  onChange={(next: boolean) => setShowRevoked(next)}
                />
              )}
              <Button size="sm" onClick={() => setCreating(true)}>{t('settings.apiKeys.actions.create')}</Button>
            </Group>
          </Group>

          {error && <Alert color="error" role="alert">{t('settings.apiKeys.loadError')}</Alert>}

          {!isPending && !error && rows.length === 0 ? (
            <EmptyState
              title={t('settings.apiKeys.empty')}
              description={t('settings.apiKeys.empty.hint')}
              action={<Button size="xs" onClick={() => setCreating(true)}>{t('settings.apiKeys.actions.create')}</Button>}
            />
          ) : (
            <DataTable<Indexed<identity.ApiKey>>
              records={rows}
              loading={isPending}
              idAccessor="id"
              emptyState={t('settings.apiKeys.empty')}
              columns={[
                { accessor: 'name', title: t('common.name') },
                { accessor: 'prefix', title: t('settings.apiKeys.columns.prefix'), render: (r) => <code>{r.prefix}…</code> },
                {
                  accessor: 'scopes',
                  title: t('settings.apiKeys.columns.scopes'),
                  // §4 item 4: the scopes array was never displayed, so a
                  // tenant could not tell what a key was allowed to do.
                  // The label came from `scope.${sc.replace(':','')}`, which
                  // builds `scope.messagessend` while the catalogue defines
                  // `scope.messagesSend`: every badge fell through to the raw
                  // scope string. SCOPES already holds the right key, so ask it.
                  render: (r) => (
                    <Group gap={4} wrap="wrap">
                      {(r.scopes ?? []).map((sc) => {
                        const known = SCOPES.find((s) => s.value === sc);
                        return (
                          <Badge key={sc} size="xs" variant="light">
                            {known ? t(known.labelKey, sc) : sc}
                          </Badge>
                        );
                      })}
                    </Group>
                  )
                },
                {
                  accessor: 'last_used_at',
                  title: t('settings.apiKeys.columns.lastUsed'),
                  /*
                   * §4 item 3, "highest-value missing column": without it,
                   * revoking is guesswork, because a dead key looks exactly
                   * like the one production depends on.
                   */
                  render: (r) => r.last_used_at
                    ? (
                      <Tooltip label={formatDateTime(r.last_used_at)} position="top">
                        <span>{formatRelative(r.last_used_at)}</span>
                      </Tooltip>
                    )
                    : <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.apiKeys.neverUsed')}</Text>
                },
                {
                  accessor: 'rate_limit_per_minute',
                  title: t('settings.apiKeys.columns.rateLimit'),
                  // §4 item 5: a tenant hitting a per-key rate limit had no
                  // way to see what it was.
                  render: (r) => t('settings.apiKeys.perMinute', { count: r.rate_limit_per_minute })
                },
                {
                  accessor: 'daily_ai_limit_minor',
                  title: t('settings.apiKeys.columns.aiLimit'),
                  // The most an AI-enabled key may spend in a day (14 October 2026).
                  render: (r) => !(r.scopes ?? []).includes('ai')
                    ? <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.apiKeys.aiLimit.noAi')}</Text>
                    : (
                      <Group gap={6} wrap="nowrap">
                        <Text role="bodySmall">
                          {r.daily_ai_limit_minor ? t('settings.apiKeys.aiLimit.perDay', { amount: credits(r.daily_ai_limit_minor) }) : t('settings.apiKeys.aiLimit.none')}
                        </Text>
                        {r.is_active && (
                          <Button size="xs" variant="subtle" onClick={() => setLimitFor(r)} aria-label={t('settings.apiKeys.aiLimit.changeNamed', { name: r.name })}>
                            {t('action.change')}
                          </Button>
                        )}
                      </Group>
                    )
                },
                {
                  accessor: 'expires_at',
                  title: t('settings.apiKeys.columns.expires'),
                  render: (r) => r.expires_at
                    ? formatDateTime(r.expires_at)
                    : <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.apiKeys.neverExpires')}</Text>
                },
                {
                  accessor: 'is_active',
                  title: t('common.status'),
                  render: (r) => (
                    <StatusBadge status={r.is_active ? 'healthy' : 'inactive'}>
                      {r.is_active ? t('status.active') : t('status.revoked')}
                    </StatusBadge>
                  )
                },
                {
                  accessor: 'actions',
                  title: t('common.actions'),
                  render: (r) => r.is_active ? (
                    <Button
                      size="xs"
                      variant="subtle"
                      color="red"
                      aria-label={t('settings.apiKeys.revokeNamed', { name: r.name })}
                      loading={revoke.isPending && revoke.variables?.id === r.id}
                      onClick={() => openConfirmModal({
                        title: t('settings.apiKeys.modal.revoke.title'),
                        children: (
                          <Stack gap={8}>
                            <Text role="bodySmall">{t('settings.apiKeys.modal.revoke.body', { name: r.name })}</Text>
                            <Text role="caption" c="var(--rr-color-text-muted)">
                              {r.last_used_at
                                ? t('settings.apiKeys.modal.revoke.lastUsed', { when: formatRelative(r.last_used_at) })
                                : t('settings.apiKeys.modal.revoke.neverUsed')}
                            </Text>
                          </Stack>
                        ),
                        labels: { confirm: t('action.revoke'), cancel: t('action.cancel') },
                        confirmProps: { color: 'red' },
                        onConfirm: () => revoke.mutate(r)
                      })}
                    >
                      {t('action.revoke')}
                    </Button>
                  ) : <></>
                }
              ]}
            />
          )}

          {/* §4 item 10: a developer-facing page with no example of how to
              use what it produces. The prefix here was `rr_live_` until the
              public API document was built; no key has ever had that prefix,
              so anyone who copied this and pasted their own key over the
              placeholder was following a pattern that could not work. */}
          <Stack gap={4}>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.apiKeys.usage')}</Text>
            <Code block className="rr-apikeys__usage">
              {`curl ${publicApiUrl()}/v1/contacts \\\n  -H "X-Api-Key: rk_live_…"`/* i18n-ignore: a curl example, copied verbatim */}
            </Code>
            <Link to="/developers">{t('settings.apiKeys.seeDocs')}</Link>
          </Stack>
        </Stack>
      </Card>

      <AiLimitModal apiKey={limitFor} onClose={() => setLimitFor(null)} />

      <CreateKeyDrawer
        opened={creating}
        onClose={() => setCreating(false)}
        onCreated={(key) => { setFreshKey(key); qc.invalidateQueries({ queryKey: ['api-keys'] }); }}
      />
    </Stack>
  );
}

function CreateKeyDrawer({
  opened,
  onClose,
  onCreated
}: {
  opened: boolean;
  onClose: () => void;
  onCreated: (key: string) => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['messages:send', 'contacts:read']);
  const [rateLimit, setRateLimit] = useState(600);
  const [expiry, setExpiry] = useState<(typeof EXPIRY_CHOICES)[number]>('never');
  const [aiLimit, setAiLimit] = useState<number | ''>('');

  const create = useMutation({
    mutationFn: () => identity.createApiKey({
      name: name.trim(),
      scopes,
      rate_limit_per_minute: rateLimit,
      daily_ai_limit_minor: scopes.includes('ai') ? toMinor(aiLimit) : null,
      expires_at: expiry === 'never'
        ? undefined
        : new Date(Date.now() + Number(expiry) * 86_400_000).toISOString()
    }),
    onSuccess: (res) => {
      onCreated(res.data.key);
      onClose();
      setName('');
      setScopes(['messages:send', 'contacts:read']);
      setAiLimit('');
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('settings.apiKeys.createFailed'), t('settings.nav.apiKeys'))
  });

  function toggleScope(value: string) {
    setScopes((prev) => prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]);
  }

  return (
    <Drawer opened={opened} onClose={onClose} title={t('settings.apiKeys.actions.create')} position="right" size="md">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <Stack gap="md" style={{ padding: 4 }}>
          <TextInput
            label={t('settings.apiKeys.form.name')}
            description={t('settings.apiKeys.form.nameHint')}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            placeholder={t('settings.apiKeys.form.namePlaceholder')}
            required
            autoFocus
          />

          <Stack gap={4}>
            <Text role="inputLabel">{t('settings.apiKeys.form.scopes')}</Text>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.apiKeys.form.scopesHint')}</Text>
            <div role="group" aria-label={t('settings.apiKeys.form.scopes')}>
              <Stack gap={6}>
                {SCOPES.map((sc) => (
                  <Switch
                    key={sc.value}
                    label={t(sc.labelKey)}
                    checked={scopes.includes(sc.value)}
                    onChange={() => toggleScope(sc.value)}
                  />
                ))}
              </Stack>
            </div>
          </Stack>

          {scopes.includes('ai') && (
            <NumberInput
              label={t('settings.apiKeys.form.aiLimit')}
              description={t('settings.apiKeys.form.aiLimitHint')}
              value={aiLimit}
              onChange={(v) => setAiLimit(v)}
              min={0}
              max={100000}
              step={1}
            />
          )}

          <NumberInput
            label={t('settings.apiKeys.form.rateLimit')}
            description={t('settings.apiKeys.form.rateLimitHint')}
            value={rateLimit}
            onChange={(v) => setRateLimit(typeof v === 'number' ? v : 600)}
            min={1}
            max={6000}
          />

          <Select
            label={t('settings.apiKeys.form.expiry')}
            description={t('settings.apiKeys.form.expiryHint')}
            value={expiry}
            onChange={(e) => setExpiry(e.currentTarget.value as (typeof EXPIRY_CHOICES)[number])}
            data={EXPIRY_CHOICES.map((c) => ({ value: c, label: t(`settings.apiKeys.expiry.${c}`) }))}
          />

          <Group justify="flex-end">
            <Button type="button" variant="default" onClick={onClose}>{t('action.cancel')}</Button>
            <Button
              type="submit"
              loading={create.isPending}
              disabled={!name.trim() || scopes.length === 0}
            >
              {t('settings.apiKeys.actions.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

/** Changes an AI-enabled key's daily AI limit (14 October 2026); empty means no limit beyond the credit itself. */
function AiLimitModal({ apiKey, onClose }: { apiKey: identity.ApiKey | null; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [value, setValue] = useState<number | ''>('');
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (apiKey && seededFor !== apiKey.id) {
    setSeededFor(apiKey.id);
    setValue(apiKey.daily_ai_limit_minor ? apiKey.daily_ai_limit_minor / 100 : '');
  }
  const save = useMutation({
    mutationFn: () => identity.setApiKeyAiLimit(apiKey!.id, toMinor(value)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      notifySuccess(t('settings.apiKeys.aiLimit.saved', { name: apiKey?.name ?? '' }), t('settings.nav.apiKeys'));
      onClose();
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.nav.apiKeys'))
  });
  return (
    <Modal opened={!!apiKey} onClose={onClose} title={t('settings.apiKeys.aiLimit.title', { name: apiKey?.name ?? '' })} size="sm">
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Stack gap="md">
          <NumberInput
            label={t('settings.apiKeys.form.aiLimit')}
            description={t('settings.apiKeys.form.aiLimitHint')}
            value={value}
            onChange={(v) => setValue(v)}
            min={0}
            max={100000}
            step={1}
            autoFocus
          />
          <Group justify="flex-end">
            <Button type="button" variant="default" onClick={onClose}>{t('action.cancel')}</Button>
            <Button type="submit" loading={save.isPending}>{t('action.save')}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

